<template>
  <!-- The upload queue's ring button and menu (SI-19), at the end of every
       PaneToolbar and PhoneNav. It shows only while uploads run or have
       just finished. App.vue's watchers open it when uploads start (except
       on the session page, where each upload is its own row); with several
       buttons mounted, the newest one on screen holds the menu. -->
  <v-menu
    v-if="uploadQueue.length > 0"
    :model-value="isOpen"
    :close-on-content-click="false"
    :open-on-hover="activeUploads.length === 0"
    location="bottom end"
    offset="8"
    @update:model-value="setOpen"
  >
    <template #activator="{ props }">
      <button ref="btn" v-bind="props" type="button" class="upload-indicator" :aria-label="uploadSummary" @click="setOpen(!isOpen)">
        <v-badge :model-value="activeUploads.length > 1" :content="activeUploads.length" color="primary" offset-x="2" offset-y="2">
          <v-progress-circular
            v-if="activeUploads.length > 0"
            :model-value="overallUploadProgress"
            :indeterminate="overallUploadProgress === 0"
            color="purple"
            size="28"
            width="3"
          >
            <v-icon size="12">mdi-arrow-up</v-icon>
          </v-progress-circular>
          <v-icon v-else-if="hasUploadErrors" class="status-icon error">mdi-alert-circle-outline</v-icon>
          <v-icon v-else class="status-icon complete">mdi-check-circle-outline</v-icon>
        </v-badge>
      </button>
    </template>

    <v-card class="upload-details" elevation="8">
      <div class="upload-details-header">
        <span>{{ uploadSummary }}</span>
        <button type="button" class="upload-details-close-btn" title="Close" aria-label="Close" @click="setOpen(false)">
          <v-icon size="14">mdi-close</v-icon>
        </button>
      </div>
      <div v-for="file in uploadQueue" :key="file.uuid" class="upload-details-row">
        <v-icon v-if="file.status === 'complete'" size="16" class="status-icon complete">mdi-check</v-icon>
        <v-icon v-else-if="file.status === 'error'" size="16" class="status-icon error">mdi-alert-circle-outline</v-icon>
        <v-progress-circular
          v-else-if="file.status === 'uploading'"
          :model-value="file.progress"
          :indeterminate="!file.progress"
          color="purple"
          size="16"
          width="2"
        />
        <v-progress-circular
          v-else-if="file.status === 'processing' || file.status === 'processed'"
          indeterminate
          color="purple"
          size="16"
          width="2"
        />
        <v-icon v-else size="16" class="status-icon adding">mdi-clock-outline</v-icon>
        <div class="upload-file-col">
          <span class="upload-file-name" :title="file.name || file.file?.name">
            {{ file.name || file.file?.name || "Audio file" }}
          </span>
          <div v-if="file.status === 'uploading' || file.status === 'processing'" class="upload-mini-progress">
            <div
              class="upload-mini-progress-fill"
              :class="{ 'is-indeterminate': file.status === 'processing' }"
              :style="{ width: `${file.status === 'processing' ? 100 : file.progress || 0}%` }"
            ></div>
          </div>
        </div>
        <span class="upload-file-state">{{ fileStateLabel(file) }}</span>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { ACTIVE_UPLOAD_STATES as ACTIVE_STATES, shellState } from "./shellState.js";

let seq = 0;
const mounted = [];

function visible(inst) {
  const el = inst.$refs.btn;
  return !!el && el.getClientRects().length > 0;
}

// The newest button on screen holds the menu.
function chooseOwner() {
  for (let i = mounted.length - 1; i >= 0; i--) {
    if (visible(mounted[i])) {
      shellState.uploads.owner = mounted[i].uid;
      return;
    }
  }
  shellState.uploads.owner = mounted.length ? mounted[mounted.length - 1].uid : null;
}



export default {
  name: "UploadQueueButton",
  data: () => ({ uid: ++seq }),
  computed: {
    uploadQueue() {
      return this.$store.state.files;
    },
    activeUploads() {
      return this.uploadQueue.filter((f) => ACTIVE_STATES.includes(f.status));
    },
    overallUploadProgress() {
      if (this.activeUploads.length === 0) return 0;
      return this.activeUploads.reduce((sum, f) => sum + (f.progress || 0), 0) / this.activeUploads.length;
    },
    hasUploadErrors() {
      return this.uploadQueue.some((f) => f.status === "error");
    },
    uploadSummary() {
      if (this.activeUploads.length > 0) {
        const done = this.uploadQueue.filter((f) => f.status === "complete").length;
        return `Uploading ${done + 1} of ${this.uploadQueue.length}`;
      }
      if (this.hasUploadErrors) return "Some uploads failed";
      return "Uploads complete";
    },
    isOpen() {
      return shellState.uploads.open && shellState.uploads.owner === this.uid;
    },
  },
  mounted() {
    mounted.push(this);
    this.$nextTick(chooseOwner);
    this.stopWatch = this.$watch(
      () => shellState.uploads.open,
      (open) => {
        if (open) chooseOwner();
      },
    );
  },
  beforeUnmount() {
    const i = mounted.indexOf(this);
    if (i >= 0) mounted.splice(i, 1);
    if (this.stopWatch) this.stopWatch();
    if (shellState.uploads.owner === this.uid) chooseOwner();
  },
  methods: {
    setOpen(v) {
      if (v) shellState.uploads.owner = this.uid;
      shellState.uploads.open = !!v;
    },
    fileStateLabel(file) {
      switch (file.status) {
        case "complete":
          return "Done";
        case "error":
          return file.error || "Failed";
        case "uploading":
          return `${Math.round(file.progress || 0)}%`;
        case "processing":
        case "processed":
          return file.isArchive ? "Unpacking…" : "Processing…";
        default:
          return "Queued";
      }
    },
  },
};
</script>

<style scoped>
.upload-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}
:root[data-density="pointer"] .upload-indicator {
  width: 32px;
  height: 32px;
}
.status-icon {
  font-size: 20px !important;
}
.status-icon.complete {
  color: var(--cl-live) !important;
}
.status-icon.error {
  color: var(--cl-destructive) !important;
}
.status-icon.adding {
  color: var(--cl-label-2) !important;
}
.upload-details {
  min-width: 260px;
  max-width: 340px;
  padding: 0.5rem 0.75rem;
  background: var(--cl-sheet) !important;
  border: 1px solid var(--border-subtle);
}
.upload-details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--cl-label);
  padding-bottom: 0.4rem;
  margin-bottom: 0.4rem;
  border-bottom: 1px solid var(--border-subtle);
}
.upload-details-close-btn {
  background: none;
  border: none;
  color: var(--cl-label-50);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border-radius: var(--radius-sm);
}
.upload-details-close-btn:hover {
  color: var(--cl-label);
  background: var(--cl-hover);
}
.upload-details-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0;
  font-size: 0.75rem;
}
.upload-file-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.upload-file-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--cl-label-85);
  font-size: 0.75rem;
}
.upload-mini-progress {
  width: 100%;
  height: 3px;
  background: var(--cl-hover);
  border-radius: 2px;
  overflow: hidden;
}
.upload-mini-progress-fill {
  height: 100%;
  background: var(--cl-accent);
}
.upload-file-state {
  color: var(--cl-label-55);
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}
</style>
